import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, X, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  BILLING_ERROR_EVENT,
  type BillingErrorEventDetail,
} from '@/api/client';
import { planLabel } from '@/api/queries/billing';
import { isDesktopApp } from '@/lib/desktop';

// ─────────────────────────────────────────────────────────────
// Bannière d'upsell globale — réagit aux erreurs 402/403 émises
// par l'API (interceptée dans api/client.ts, AUCUN crash) :
//  - 403 PLAN_INSUFFISANT   → « Fonctionnalité de l'offre X »
//  - 402 ABONNEMENT_SUSPENDU → compte en lecture seule
// Montée une seule fois dans AppLayout.
// ─────────────────────────────────────────────────────────────

interface BannerState {
  code: string;
  message: string;
  requiredPlan: string | null;
}

/** Anti-rafale : plusieurs requêtes peuvent échouer d'un coup. */
const THROTTLE_MS = 3000;

export function UpsellBanner() {
  const [banner, setBanner] = useState<BannerState | null>(null);

  useEffect(() => {
    let lastShownAt = 0;
    const onBillingError = (event: Event) => {
      const detail = (event as CustomEvent<BillingErrorEventDetail>).detail;
      if (!detail) return;
      const now = Date.now();
      if (now - lastShownAt < THROTTLE_MS) return;
      lastShownAt = now;
      setBanner({
        code: detail.code,
        message: detail.message,
        requiredPlan: detail.requiredPlan ?? null,
      });
    };
    window.addEventListener(BILLING_ERROR_EVENT, onBillingError);
    return () => window.removeEventListener(BILLING_ERROR_EVENT, onBillingError);
  }, []);

  // No subscription in the desktop app: nothing to upsell.
  if (!banner || isDesktopApp()) return null;

  const isSuspension = banner.code === 'ABONNEMENT_SUSPENDU';

  return (
    <div
      role="alert"
      className={cn(
        'mb-4 flex items-start gap-3 rounded-xl border p-4',
        isSuspension
          ? 'border-red-200 bg-red-50'
          : 'border-amber-200 bg-amber-50'
      )}
    >
      <Lock
        className={cn(
          'mt-0.5 h-5 w-5 flex-shrink-0',
          isSuspension ? 'text-red-600' : 'text-amber-600'
        )}
      />
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm font-semibold',
            isSuspension ? 'text-red-800' : 'text-amber-800'
          )}
        >
          {isSuspension
            ? 'Abonnement suspendu — compte en lecture seule'
            : banner.requiredPlan
              ? `Fonctionnalité de l'offre « ${planLabel(banner.requiredPlan)} »`
              : 'Fonctionnalité non incluse dans votre offre'}
        </p>
        <p
          className={cn(
            'mt-0.5 text-sm',
            isSuspension ? 'text-red-700' : 'text-amber-700'
          )}
        >
          {banner.message}
        </p>
        <Link
          to="/parametres/abonnement"
          onClick={() => setBanner(null)}
          className={cn(
            'mt-2 inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-2',
            isSuspension
              ? 'text-red-800 hover:text-red-900'
              : 'text-amber-800 hover:text-amber-900'
          )}
        >
          <CreditCard className="h-4 w-4" />
          Gérer mon abonnement
        </Link>
      </div>
      <button
        type="button"
        onClick={() => setBanner(null)}
        aria-label="Fermer la bannière"
        className={cn(
          'rounded p-1 transition-colors',
          isSuspension
            ? 'text-red-500 hover:bg-red-100 hover:text-red-700'
            : 'text-amber-500 hover:bg-amber-100 hover:text-amber-700'
        )}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default UpsellBanner;
